import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Archive, ArrowLeft, Pencil, Plus, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api, extractApiError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { canArchiveCRM, canCreateOrEditCRM, canDeleteActivity, canRestoreCRM } from "../lib/crm-permissions";
import { ASSIGNEE_UNASSIGNED, getAssigneeLabel, getTaskAssigneeLabel, toAssignedUserId } from "../lib/tasks-assignees";
import { isTaskOverdue } from "../lib/task-detail";
import PageHeader from "../components/layout/PageHeader";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import StatusPill from "../components/status/StatusPill";
import Timeline from "../components/activity/Timeline";
import AddActivityDialog from "../components/activity/AddActivityDialog";
import QuickNoteDialog from "../components/activity/QuickNoteDialog";
import { formatDate } from "../lib/format";
import LeadFormDialog from "../components/leads/LeadFormDialog";
import { PRIORITY, TASK_STATUS } from "../lib/enums";
import {
  getConsentSourceLabel,
  LEAD_STATUS_LABELS,
  PRIORITY_LABELS,
  TASK_STATUS_LABELS,
} from "../lib/labels";
import {
  DETAIL_ACTIVITY_PAGE_SIZE,
  mergeActivities,
  normalizeActivityPageResponse,
} from "../lib/activity-pagination";

const NONE = "__none__";
const TASK_FORM_INITIAL = {
  title: "",
  description: "",
  due_date: "",
  priority: PRIORITY.MEDIUM,
  status: TASK_STATUS.OPEN,
  assigned_user_id: ASSIGNEE_UNASSIGNED,
};

function getConsentStatusLabel(status) {
  if (status === "granted") return "Erlaubt";
  if (status === "revoked") return "Widerrufen";
  return "Nicht erteilt";
}

export default function ContactDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { role, isSuperAdmin } = useAuth();
  const perm = { role, isSuperAdmin };
  const canDeleteActivities = canDeleteActivity(perm);
  const [contact, setContact] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [leads, setLeads] = useState([]);
  const [activities, setActivities] = useState([]);
  const [activitiesHasMore, setActivitiesHasMore] = useState(false);
  const [activitiesCursor, setActivitiesCursor] = useState(null);
  const [activitiesLoadingMore, setActivitiesLoadingMore] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(true);
  const [assigneesError, setAssigneesError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [leadDialogOpen, setLeadDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskForm, setTaskForm] = useState(TASK_FORM_INITIAL);
  const [taskSaving, setTaskSaving] = useState(false);
  const [activityDialogOpen, setActivityDialogOpen] = useState(false);
  const [quickNoteDialogOpen, setQuickNoteDialogOpen] = useState(false);

  const assigneesById = useMemo(() => {
    const map = {};
    for (const u of assignees || []) {
      if (u?.id) map[u.id] = u;
    }
    return map;
  }, [assignees]);

  const assigneeOptions = useMemo(() => {
    const items = (assignees || [])
      .filter((u) => u?.id)
      .map((u) => ({ id: u.id, label: getAssigneeLabel(u) }));
    items.sort((a, b) => a.label.localeCompare(b.label, "de"));
    return items;
  }, [assignees]);

  const loadAssignees = useCallback(async () => {
    setAssigneesLoading(true);
    setAssigneesError("");
    try {
      const r = await api.get("/api/settings/users");
      setAssignees(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setAssignees([]);
      setAssigneesError(extractApiError(e, "Teammitglieder konnten nicht geladen werden."));
    } finally {
      setAssigneesLoading(false);
    }
  }, []);

  const loadActivities = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (append) setActivitiesLoadingMore(true);
      try {
        const params = { contact_id: id, limit: DETAIL_ACTIVITY_PAGE_SIZE };
        if (cursor?.created_at && cursor?.id) {
          params.before_created_at = cursor.created_at;
          params.before_id = cursor.id;
        }
        const r = await api.get("/api/activities/page", { params });
        const page = normalizeActivityPageResponse(r.data);
        setActivities((prev) => (append ? mergeActivities(prev, page.items) : page.items));
        setActivitiesHasMore(page.hasMore);
        setActivitiesCursor(page.nextCursor);
      } catch (e) {
        toast.error(extractApiError(e, "Aktivitäten konnten nicht geladen werden."));
        if (!append) {
          setActivities([]);
          setActivitiesHasMore(false);
          setActivitiesCursor(null);
        }
      } finally {
        if (append) setActivitiesLoadingMore(false);
      }
    },
    [id]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, co, ld, tk] = await Promise.all([
        api.get(`/api/contacts/${id}`),
        api.get("/api/companies"),
        api.get("/api/leads", { params: { contact_id: id } }),
        api.get(`/api/tasks`, { params: { related_contact_id: id } }),
        loadActivities(),
      ]);
      setContact(c.data);
      setCompanies(co.data);
      setLeads(ld.data);
      setTasks(Array.isArray(tk.data) ? tk.data : []);
    } catch (e) {
      toast.error(extractApiError(e, "Kontakt konnte nicht geladen werden."));
      navigate("/kontakte");
    } finally {
      setLoading(false);
    }
  }, [id, loadActivities, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadAssignees();
  }, [loadAssignees]);

  const isArchived = Boolean(contact?.archived_at);
  const canEdit = canCreateOrEditCRM(perm) && !isArchived;
  const canArchive = canArchiveCRM(perm) && !isArchived;
  const canRestore = canRestoreCRM(perm) && isArchived;
  const company = contact?.company_id ? companies.find((c) => c.id === contact.company_id) : null;

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!canEdit) return;
    if (!taskForm.title.trim()) return toast.error("Bitte einen Titel angeben.");
    setTaskSaving(true);
    try {
      const assignedUserId = toAssignedUserId(taskForm.assigned_user_id);
      const payload = {
        title: taskForm.title,
        description: taskForm.description,
        due_date: taskForm.due_date ? new Date(taskForm.due_date).toISOString() : null,
        priority: taskForm.priority,
        status: taskForm.status,
        related_contact_id: id,
        ...(assignedUserId ? { assigned_user_id: assignedUserId } : {}),
      };
      await api.post("/api/tasks", payload);
      toast.success("Aufgabe erstellt.");
      setTaskForm(TASK_FORM_INITIAL);
      setTaskDialogOpen(false);
      load();
    } catch (err) {
      toast.error(extractApiError(err, "Aufgabe konnte nicht erstellt werden."));
    } finally {
      setTaskSaving(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setSaving(true);
    try {
      const payload = {
        first_name: contact.first_name,
        last_name: contact.last_name,
        email: contact.email,
        phone: contact.phone,
        mobile_phone: contact.mobile_phone,
        position: contact.position,
        department: contact.department,
        linkedin_url: contact.linkedin_url,
        notes: contact.notes,
        company_id: contact.company_id || null,
        consent_marketing: !!contact.consent_marketing,
        consent_marketing_at: contact.consent_marketing_at || null,
        consent_marketing_source: contact.consent_marketing_source || null,
        consent_marketing_text_snapshot: contact.consent_marketing_text_snapshot || null,
        consent_marketing_revoked_at: contact.consent_marketing_revoked_at || null,
      };
      await api.patch(`/api/contacts/${id}`, payload);
      toast.success("Kontakt gespeichert.");
      load();
    } catch (e) { toast.error(extractApiError(e, "Speichern fehlgeschlagen.")); }
    finally { setSaving(false); }
  };

  const handleArchive = async () => {
    if (!canArchive) return;
    setArchiving(true);
    try {
      await api.post(`/api/contacts/${id}/archive`);
      toast.success("Kontakt wurde archiviert.");
      setArchiveOpen(false);
      load();
    } catch (e) {
      toast.error(extractApiError(e, "Archivieren fehlgeschlagen."));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async () => {
    if (!canRestore) return;
    setRestoring(true);
    try {
      await api.post(`/api/contacts/${id}/restore`);
      toast.success("Kontakt wurde wiederhergestellt.");
      load();
    } catch (e) {
      toast.error(extractApiError(e, "Wiederherstellen fehlgeschlagen."));
    } finally {
      setRestoring(false);
    }
  };

  const handleDeleteActivity = useCallback(async (activity) => {
    try {
      await api.delete(`/api/activities/${activity.id}`);
      setActivities((prev) => prev.filter((item) => item.id !== activity.id));
      toast.success("Aktivität gelöscht.");
    } catch (e) {
      const msg = extractApiError(e, "Aktivität konnte nicht gelöscht werden.");
      toast.error(msg);
      throw e;
    }
  }, []);

  if (loading || !contact) return <div className="text-sm text-muted-foreground py-12 text-center">Wird geladen …</div>;

  return (
    <div>
      <button onClick={() => navigate("/kontakte")} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center mb-3">
        <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zu Kontakten
      </button>
      {isArchived ? (
        <Alert className="mb-4 border-border/60 bg-muted/20">
          <AlertTitle>Archiviert</AlertTitle>
          <AlertDescription>Dieser Kontakt ist archiviert.</AlertDescription>
        </Alert>
      ) : null}
      <PageHeader
        title={`${contact.first_name} ${contact.last_name}`}
        description={`Erstellt am ${formatDate(contact.created_at)}`}
        actions={
          <>
            {canArchive ? (
              <Button variant="outline" onClick={() => setArchiveOpen(true)}>
                <Archive className="w-4 h-4 mr-1.5" /> Archivieren
              </Button>
            ) : null}
            {canRestore ? (
              <Button variant="outline" onClick={handleRestore} disabled={restoring}>
                <RotateCcw className="w-4 h-4 mr-1.5" /> Wiederherstellen
              </Button>
            ) : null}
            {canEdit ? (
              <Button onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4 mr-1.5" /> Speichern
              </Button>
            ) : null}
          </>
        }
      />
      {contact.company_id ? (
        <div className="mb-4">
          <button
            onClick={() => navigate(`/firmen/${contact.company_id}`)}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            Firma öffnen{company?.name ? `: ${company.name}` : ""}
          </button>
        </div>
      ) : null}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <Card className="lg:col-span-7">
          <CardHeader><CardTitle>Stammdaten</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <F label="Vorname"><Input value={contact.first_name} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} disabled={!canEdit} /></F>
            <F label="Nachname"><Input value={contact.last_name} onChange={(e) => setContact({ ...contact, last_name: e.target.value })} disabled={!canEdit} /></F>
            <F label="E-Mail"><Input value={contact.email || ""} onChange={(e) => setContact({ ...contact, email: e.target.value })} disabled={!canEdit} /></F>
            <F label="Telefon"><Input value={contact.phone || ""} onChange={(e) => setContact({ ...contact, phone: e.target.value })} disabled={!canEdit} /></F>
            <F label="Mobil"><Input value={contact.mobile_phone || ""} onChange={(e) => setContact({ ...contact, mobile_phone: e.target.value })} disabled={!canEdit} /></F>
            <F label="Position"><Input value={contact.position || ""} onChange={(e) => setContact({ ...contact, position: e.target.value })} disabled={!canEdit} /></F>
            <F label="Abteilung"><Input value={contact.department || ""} onChange={(e) => setContact({ ...contact, department: e.target.value })} disabled={!canEdit} /></F>
            <F label="Firma">
              <Select value={contact.company_id || NONE} onValueChange={(v) => setContact({ ...contact, company_id: v === NONE ? null : v })} disabled={!canEdit}>
                <SelectTrigger><SelectValue placeholder="Keine Firma" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Keine Firma</SelectItem>
                  {companies.map((co) => <SelectItem key={co.id} value={co.id}>{co.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="LinkedIn" full><Input value={contact.linkedin_url || ""} onChange={(e) => setContact({ ...contact, linkedin_url: e.target.value })} disabled={!canEdit} /></F>
            <F label="Notizen" full><Textarea rows={4} value={contact.notes || ""} onChange={(e) => setContact({ ...contact, notes: e.target.value })} disabled={!canEdit} /></F>
            <label className="md:col-span-2 inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!contact.consent_marketing} onChange={(e) => setContact({ ...contact, consent_marketing: e.target.checked })} disabled={!canEdit} />
              Einwilligung Marketing-Kommunikation
            </label>
            {contact.consent_marketing_at || contact.consent_marketing_source || contact.consent_marketing_revoked_at ? (
              <div className="md:col-span-2 rounded-lg border border-border/60 bg-card/50 p-3 text-xs text-muted-foreground space-y-1">
                {contact.consent_marketing_at ? (
                  <div>Erteilt am: {formatDate(contact.consent_marketing_at)}</div>
                ) : null}
                {contact.consent_marketing_source ? (
                  <div>Quelle: {getConsentSourceLabel(contact.consent_marketing_source)}</div>
                ) : null}
                {contact.consent_marketing_revoked_at ? (
                  <div>Widerrufen am: {formatDate(contact.consent_marketing_revoked_at)}</div>
                ) : null}
              </div>
            ) : null}
            <div className="md:col-span-2 rounded-lg border border-border/60 bg-card/50 p-3 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">Einwilligungen</div>
                  <div className="text-xs text-muted-foreground">
                    Kompakte Übersicht, die vollständige Historie liegt im Consent Center.
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/einwilligungen")}
                >
                  Einwilligungshistorie öffnen
                </Button>
              </div>
              <div className="grid gap-3 md:grid-cols-3 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">Marketing-Kommunikation</div>
                  <div>{getConsentStatusLabel(contact.consent_marketing ? "granted" : (contact.consent_marketing_revoked_at ? "revoked" : "not_granted"))}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">E-Mail-Tracking</div>
                  <div>{getConsentStatusLabel(contact.consent_tracking_status)}</div>
                  {contact.consent_tracking_at ? (
                    <div className="text-xs text-muted-foreground">
                      Letzte Änderung: {formatDate(contact.consent_tracking_at)}
                    </div>
                  ) : null}
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Datenschutz / Anfrage</div>
                  <div>{getConsentStatusLabel(contact.consent_privacy_status)}</div>
                  {contact.consent_privacy_at ? (
                    <div className="text-xs text-muted-foreground">
                      Bestätigt am: {formatDate(contact.consent_privacy_at)}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="lg:col-span-5 space-y-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Verknüpfte Leads ({leads.length})</CardTitle>
              {canEdit ? (
                <Button size="sm" variant="outline" onClick={() => setLeadDialogOpen(true)}>
                  <Plus className="w-4 h-4" /> Lead erstellen
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              {leads.length === 0 ? (
                <div className="text-sm text-muted-foreground">Keine Leads.</div>
              ) : (
                <ul className="divide-y divide-border/60">
                  {leads.map((l) => {
                    const st = LEAD_STATUS_LABELS[l.status] || { de: l.status, tone: "muted" };
                    return (
                      <li
                        key={l.id}
                        className="py-2 text-sm cursor-pointer hover:bg-muted/40 rounded px-2"
                        onClick={() => navigate(`/leads/${l.id}`)}
                      >
                        <div className="font-medium">{l.title}</div>
                        <div className="text-xs text-muted-foreground">{st.de}</div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Verknüpfte Aufgaben</CardTitle>
              {canEdit ? (
                <Button size="sm" variant="outline" onClick={() => setTaskDialogOpen(true)}>
                  <Plus className="w-4 h-4" /> Aufgabe hinzufügen
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              {tasks.length === 0 ? (
                <div className="text-sm text-muted-foreground">Keine Aufgaben verknüpft.</div>
              ) : (
                <ul className="divide-y divide-border/60">
                  {tasks.map((t) => {
                    const stt = TASK_STATUS_LABELS[t.status] || { de: t.status, tone: "muted" };
                    const prt = PRIORITY_LABELS[t.priority] || { de: t.priority, tone: "muted" };
                    const overdue = isTaskOverdue(t.due_date, t.status);
                    const assigneeLabel = t.assigned_user_id
                      ? getTaskAssigneeLabel({ assignedUserId: t.assigned_user_id, usersById: assigneesById })
                      : "";
                    return (
                      <li key={t.id} className="py-2 flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <button
                            type="button"
                            className="text-sm font-medium break-words hover:underline text-left"
                            onClick={() => navigate(`/aufgaben/${t.id}`)}
                          >
                            {t.title}
                          </button>
                          {t.due_date && (
                            <div className={`text-xs ${overdue ? "text-destructive" : "text-muted-foreground"}`}>
                              Fällig {formatDate(t.due_date)}
                            </div>
                          )}
                          {t.assigned_user_id ? (
                            <div className="text-xs text-muted-foreground">Zugewiesen an: {assigneeLabel}</div>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {overdue ? <StatusPill tone="destructive">Überfällig</StatusPill> : null}
                          <StatusPill tone={prt.tone}>{prt.de}</StatusPill>
                          <StatusPill tone={stt.tone}>{stt.de}</StatusPill>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle>Aktivitäten</CardTitle>
              {canEdit ? (
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setQuickNoteDialogOpen(true)}>
                    <Pencil className="w-4 h-4 mr-2" /> Notiz
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setActivityDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-2" /> Aktivität
                  </Button>
                </div>
              ) : null}
            </CardHeader>
            <CardContent>
              <Timeline
                items={activities}
                usersById={assigneesById}
                currentContext={{ type: "contact", id: contact.id }}
                canDelete={canDeleteActivities}
                onDelete={handleDeleteActivity}
              />
              <div className="mt-4 flex flex-col items-center gap-2">
                {activitiesHasMore ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => loadActivities({ cursor: activitiesCursor, append: true })}
                    disabled={activitiesLoadingMore}
                  >
                    {activitiesLoadingMore ? "Weitere Aktivitäten werden geladen …" : "Mehr Aktivitäten laden"}
                  </Button>
                ) : activities.length > 0 ? (
                  <div className="text-xs text-muted-foreground">Alle Aktivitäten geladen</div>
                ) : null}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <AlertDialog
        open={archiveOpen}
        onOpenChange={(open) => {
          if (archiving) return;
          setArchiveOpen(open);
        }}
      >
        <AlertDialogContent className="border-border/60 shadow-lg shadow-black/10 outline-none ring-0 ring-offset-0">
          <AlertDialogHeader>
            <AlertDialogTitle>Kontakt archivieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Dieser Kontakt bleibt im System erhalten, wird aber standardmäßig nicht mehr in Listen angezeigt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              disabled={archiving}
              onClick={(event) => {
                event.preventDefault();
                handleArchive();
              }}
            >
              {archiving ? "Archivieren …" : "Archivieren"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuickNoteDialog
        open={quickNoteDialogOpen}
        onOpenChange={setQuickNoteDialogOpen}
        relationPayload={{
          related_contact_id: id,
          ...(contact?.company_id ? { related_company_id: contact.company_id } : {}),
        }}
        onCreated={() => loadActivities()}
      />
      <AddActivityDialog
        open={activityDialogOpen}
        onOpenChange={setActivityDialogOpen}
        relationPayload={{
          related_contact_id: id,
          ...(contact?.company_id ? { related_company_id: contact.company_id } : {}),
        }}
        onCreated={() => loadActivities()}
      />

      <LeadFormDialog
        open={leadDialogOpen}
        onOpenChange={setLeadDialogOpen}
        onSaved={() => {
          setLeadDialogOpen(false);
          load();
        }}
        initialContact={contact}
        initialCompany={company}
      />

      <Dialog
        open={taskDialogOpen}
        onOpenChange={(open) => {
          if (taskSaving) return;
          setTaskDialogOpen(open);
          if (!open) setTaskForm(TASK_FORM_INITIAL);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Aufgabe hinzufügen</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-3">
            <F label="Titel *"><Input value={taskForm.title} onChange={(e) => setTaskForm((s) => ({ ...s, title: e.target.value }))} autoFocus /></F>
            <F label="Beschreibung"><Textarea rows={3} value={taskForm.description} onChange={(e) => setTaskForm((s) => ({ ...s, description: e.target.value }))} /></F>
            <div className="grid md:grid-cols-2 gap-3">
              <F label="Fälligkeitsdatum"><Input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm((s) => ({ ...s, due_date: e.target.value }))} /></F>
              <F label="Zugewiesen an">
                <Select
                  value={taskForm.assigned_user_id}
                  onValueChange={(v) => setTaskForm((s) => ({ ...s, assigned_user_id: v }))}
                  disabled={assigneesLoading || Boolean(assigneesError)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={ASSIGNEE_UNASSIGNED}>Nicht zugewiesen</SelectItem>
                    {assigneeOptions.map((u) => <SelectItem key={u.id} value={u.id}>{u.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {assigneesError ? <div className="text-xs text-muted-foreground mt-1">{assigneesError}</div> : null}
              </F>
              <F label="Priorität">
                <Select value={taskForm.priority} onValueChange={(v) => setTaskForm((s) => ({ ...s, priority: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.values(PRIORITY).map((p) => <SelectItem key={p} value={p}>{PRIORITY_LABELS[p].de}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Status">
                <Select value={taskForm.status} onValueChange={(v) => setTaskForm((s) => ({ ...s, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.values(TASK_STATUS).map((s) => <SelectItem key={s} value={s}>{TASK_STATUS_LABELS[s].de}</SelectItem>)}</SelectContent>
                </Select>
              </F>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)} disabled={taskSaving}>Abbrechen</Button>
              <Button type="submit" disabled={taskSaving}>{taskSaving ? "Speichern …" : "Erstellen"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children, full = false }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
